import React, { useEffect, useState } from 'react';
import useRequestDetails from '../hooks/useRequestDetails';
import {
    NButton,
    NLink,
    NStatusTag,
    NTable,
} from '@innersource-nn/novoui-react';
import { ShareItem, ShareObjectStatus } from 'types/Share';
import Pagination from 'components/pager/Pagination';
import { ShareableObjectFilter } from 'types/Filters';
import Spinner from 'components/loader/Spinner';
import InboxIcon from 'assets/icons/InboxIcon';
import { getTagColor } from 'utils/getTagColorForShare';
import { useModal } from 'hooks/useModal';
import { useParams } from 'react-router-dom';
import useApproveShareObjectMutation from '../hooks/useApproveShareObjectMutation';

const tableHeaders = [
    { header: 'Name', key: 'itemName' },
    { header: 'Type', key: 'itemType' },
    { header: 'Requester', key: 'owner' },
    { header: 'Approver', key: 'approver' },
    { header: 'Status', key: 'status' },
];

interface RequestDetailsProps {
    role: string;
    status: string;
}
const RequestDetails = ({ role, status }: RequestDetailsProps) => {
    const refreshItems = async () => {
        const updatedFilter = { ...filter };
        setFilter(updatedFilter);
    };
    const [filter, setFilter] = useState<ShareableObjectFilter>({
        isShared: true,
        pageSize: 10,
        page: 1,
    });
    const {
        shares,
        items,
        created,
        loading,
        datasetUri,
        goToS3Console,
        removeItemFromShareObject,
    } = useRequestDetails(filter, refreshItems);

    const { openRejectRequestModal } = useModal();
    const [requestApprovalLoading, setRequestApprovalLoading] = useState(false);
    const params = useParams();
    const { openEditItemsShareModal } = useModal();
    const { approveShareObject } = useApproveShareObjectMutation(
        params.uri || '',
    );

    const rejectRequest = () => {
        openRejectRequestModal({
            shareUri: params.uri ?? '',
            onClose: refreshItems,
            onSubmit: refreshItems,
        });
    };

    const useAcceptRequest = async () => {
        setRequestApprovalLoading(true);
        await approveShareObject();
        await refreshItems();
        setRequestApprovalLoading(false);
    };
    const editRequest = () => {
        openEditItemsShareModal({
            shareUri: params.uri ?? '',
            resourceUri: datasetUri,
            onSubmit: refreshItems,
        });
    };

    // We only want to add this action column if the user is an approver
    useEffect(() => {
        if (
            role === 'Approvers' &&
            !tableHeaders.some((header) => header.key === 'action')
        ) {
            const extraHeader = { header: 'Quick action', key: 'action' };
            tableHeaders.push(extraHeader);
        }
    }, [role]);

    const adjustItems = (items: ShareItem[]) => {
        return items.map((item) => {
            return {
                ...item,
                itemType:
                    //  When the type is StorageLocation or  S3Bucket and it has been Shared this itemit has to be a link to the S3Bucket
                    item.status === 'Shared' &&
                    (item.itemType === 'StorageLocation' ||
                        item.itemType === 'S3Bucket') ? (
                        <NLink
                            href="#"
                            label={item.itemType}
                            onClick={() => goToS3Console(item.itemName)}
                            icon="external"
                            iconPosition="left"
                            behavior="standalone"
                            size="small"
                        />
                    ) : (
                        item.itemType
                    ),

                approver: item.approver.split('@')[0].toUpperCase(),
                owner: item.owner.split('@')[0].toUpperCase(),
                status: (
                    <NStatusTag
                        label={item.status}
                        state={getTagColor(item.status)}
                    />
                ),
                action: (
                    <NButton
                        square
                        className="outline-red-button"
                        size="tiny"
                        leftIcon="circleRemove"
                        disabled={item.status === 'Removing'}
                        onClick={() =>
                            removeItemFromShareObject(item.shareItemUri)
                        }
                    >
                        Remove
                    </NButton>
                ),
            };
        });
    };

    return (
        <div className="info-container">
            {loading ? (
                <Spinner />
            ) : (
                <div>
                    <div className="flex justify-between">
                        <div>
                            <h3 className="text-2xl font-bold">
                                Request details
                            </h3>
                            <p className="mb-16">Created: {created}</p>
                        </div>
                        {items.count !== 0 && (
                            <div className="grid grid-flow-col gap-16">
                                {role === 'Approvers' &&
                                    status ===
                                        ShareObjectStatus.PendingApproval && (
                                        <>
                                            <NButton
                                                isLoading={
                                                    requestApprovalLoading
                                                }
                                                leftIcon="tick"
                                                square
                                                variant="outlineSubtle"
                                                className="border-forest-green-600 text-forest-green-600 hover:bg-forest-green-200 hover:border-forest-green-600"
                                                onClick={useAcceptRequest}
                                            >
                                                Accept request
                                            </NButton>
                                            <NButton
                                                square
                                                leftIcon="closeSmall"
                                                className="outline-red-button"
                                                variant="destructive"
                                                onClick={rejectRequest}
                                            >
                                                Deny request
                                            </NButton>
                                        </>
                                    )}
                                <NButton
                                    square
                                    leftIcon="edit"
                                    variant="outline"
                                    onClick={editRequest}
                                >
                                    Edit share request
                                </NButton>
                            </div>
                        )}
                    </div>
                    {items.count === 0 ? (
                        <div className="border border-faded-blue-300 rounded-8 py-32">
                            <div className="items-center">
                                <div className="flex flex-col items-center">
                                    <div className="round-div-icon-middle bg-light-blue-200 h-[5rem] w-[5rem] ">
                                        <InboxIcon className="w-40 h-40" />
                                    </div>
                                    <p className="text-true-blue w-1/2 text-center mt-8">
                                        It looks like you have removed all items
                                        from your request. Please add more items
                                        to ensure that your share request is
                                        complete.
                                    </p>
                                    {/* TODO: Add the functionality */}
                                    <NButton
                                        variant="fill"
                                        square
                                        leftIcon="circleAdd"
                                        className="mt-16"
                                        onClick={editRequest}
                                    >
                                        Add items
                                    </NButton>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <NTable
                                rowKey="itemName"
                                headers={tableHeaders}
                                columnBorder={true}
                                items={adjustItems(shares)}
                            />
                            {!loading && items.count > 0 && (
                                <Pagination
                                    items={items}
                                    filter={filter}
                                    setFilter={setFilter}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
export default RequestDetails;
